import {
  Box,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import React, { FC } from "react";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";
import { motion } from "motion/react";
import { Button } from "gatsby-theme-material-ui";
import useHash from "@src/hooks/hash";
import { ArcherElement } from "react-archer";

interface Props {
  navItem: any;
  currentDepth?: number;
}

const NavItemDimension: FC<Props> = ({ navItem, currentDepth = 0 }) => {
  const hash = useHash();
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.up("md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      <motion.div
        initial={{
          transform: `scale3d(1, 1, 1)`,
        }}
        whileHover={{
          transform: `scale3d(1.01, 1.01, 1)`,
        }}
      >
        <Link
          sx={{
            display: "flex",
            position: "relative",
            borderRadius: "15px",
            border: `solid 1px ${Colors.AiOutline}`,
            fontSize: {
              xs: "12px",
              lg: "15px",
            },
            fontWeight: 400,
            padding: "20px",
            minHeight: {
              xs: "30px",
              lg: "40px",
            },
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            textDecoration: "none",
            width: {
              xs: "300px",
              md: "350px",
              lg: "440px",
            },
            opacity: 1,
            "&:hover": {
              opacity: 1,
            },
          }}
          href={`#${navItem.data.categoryId}`}
          data-cid={navItem.data.categoryId}
        >
          <Stack
            sx={{
              gap: "20px",
            }}
          >
            <Stack>
              <Typography
                variant="h1"
                sx={{
                  textAlign: "left",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    background:
                      "linear-gradient(90deg, #B0E6FF 0%, #05E1F5 30%)",
                    backgroundClip: "text",
                    textFillColor: "transparent",
                    letterSpacing: "3px",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    fontSize: {
                      xs: "12px",
                      lg: "16px",
                    },
                    lineHeight: {
                      xs: "22px",
                    },
                  }}
                >
                  Dimension
                </Box>
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(90deg, #FFCDAF 12.35%, #8C48EB 88.07%)",
                    backgroundClip: "text",
                    textFillColor: "transparent",
                    maxWidth: "fit-content",
                    display: "block",
                    letterSpacing: 1,
                    fontWeight: 600,
                    fontSize: {
                      xs: "18px",
                      lg: "24px",
                    },
                    lineHeight: {
                      xs: "150%",
                    },
                  }}
                >
                  {navItem?.data?.name}
                </Box>
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontSize: {
                    xs: "13px",
                    lg: "16px",
                  },
                  lineHeight: {
                    xs: "18px",
                    lg: "24px",
                  },
                  textAlign: "left",
                }}
              >
                {navItem?.data?.navDescription}
              </Typography>
            </Stack>

            <Box
              sx={{
                alignSelf: "flex-start",
                display: {
                  xs: "none",
                  lg: "inline-flex",
                },
              }}
            >
              <Box>
                <motion.div
                  initial={{
                    height: "40px",
                    opacity: 1,
                  }}
                  animate={{
                    height: currentDepth <= 1 ? "40px" : "0px",
                    opacity: currentDepth <= 1 ? 1 : 0,
                  }}
                >
                  <Button
                    role="button"
                    sx={{
                      border: `solid 1px ${Colors.AiOutline}`,
                      borderRadius: "100px",
                      outline: "none",
                      display: "inline-flex",
                      backgroundColor: "rgba(0, 0, 0, 0.21)",
                      boxShadow: "none",
                    }}
                  >
                    View
                  </Button>
                </motion.div>
              </Box>
            </Box>
          </Stack>
        </Link>
      </motion.div>

      <ArcherElement
        id={`nav-item-dimension-${navItem.data.categoryId}`}
        relations={
          [
            (hash ? hash : "root") == "root" &&
              isMedium && {
                targetId: navItem?.data?.categoryId,
                targetAnchor: "right",
                sourceAnchor: "left",
                className: "taxonomy-arrow",
                style: {
                  strokeColor: Colors.AiOutline,
                  strokeWidth: 1,
                  endMarker: false,
                },
              },
          ].filter(Boolean) as any[]
        }
      >
        <Box
          id={`nav-item-dimension-${navItem.data.categoryId}`}
          component={motion.div}
          sx={{
            position: "absolute",
            color: Colors.PrimaryContrast,
            display: {
              xs: "none",
              md: "flex",
            },
            height: {
              xs: "30px",
              md: "40px",
            },
            border: `solid 1px ${Colors.AiOutline}`,
            borderRadius: "100px",
            right: {
              xs: "-50px",
              md: "-200px",
            },
            top: "50%",
            transform: `translate3d(0px, -50%, 0px)`,
            justifyContent: "center",
            alignItems: "center",
            pointerEvents:
              hash !== navItem.data.categoryId &&
              navItem?.data?.childrenCount > 0
                ? "all"
                : "none",
            paddingX: "20px",
            transformOrigin: "50% 50%",
          }}
          initial={{
            transform: `scale3d(1, 1, 1) translate3d(0px, -50%, 0px)`,
            opacity: 0,
          }}
          whileHover={{
            transform: `scale3d(1.02, 1.02, 1) translate3d(0px, -50%, 0px)`,
          }}
          animate={{
            opacity: currentDepth == 0 ? 1 : 0,
          }}
        >
          <Link
            sx={{
              color: Colors.PrimaryContrast,
              textDecoration: "none",
              span: {
                textDecoration: "none",
              },
              "&:hover": {
                opacity: 1,
              },
            }}
            href={`#${navItem.data.categoryId}`}
          >
            <span>+{navItem?.data?.totalNestedChildren} Categories</span>
          </Link>
        </Box>
      </ArcherElement>
    </Box>
  );
};

export default NavItemDimension;
